import { getLocalTimeZone, parseDate, today } from '@internationalized/date'
import { Calendar, DatePicker, Input } from '@nextui-org/react'
import { I18nProvider } from '@react-aria/i18n'
import { format, parse } from 'date-fns'
import { FC, MutableRefObject, forwardRef, useEffect, useRef, useState } from 'react'
import { keyDownChange } from '../../utils/selectTank/common'

export type EventType = {
  id: string
  name: string
  value: string
}

export type InputState = 'editing' | 'completed' | 'disabled'

export const INPUT_STATE_CLASSES = {
  editing: 'bg-tdplbisque focus:ring-2 focus:ring-tdplindigo',
  completed:
    'bg-tdplbisque cursor-pointer hover:bg-tdpllightgrey hover:border-tdplindigo relative group',
  disabled: 'bg-gray-100 border border-gray-300 cursor-not-allowed opacity-60',
} as const

export type IputBaseProps = {
  id: number
  inputRefs: MutableRefObject<(HTMLInputElement | null)[]>
  index: number
  insertData: (value: EventType) => void
  col: string
  value: { [key: string]: string | number | null }
  placeholder?: string
  disabled?: boolean // 입력불가 상태 추가
}

export const getInputState = (
  value: { [key: string]: string | number | null },
  col: string,
  disabled?: boolean,
): InputState => {
  if (disabled) return 'disabled'
  if (value[col] !== null) return 'completed'
  return 'editing'
}

type BaseCellProps = {
  inputRefs: MutableRefObject<(HTMLInputElement | null)[]>
  hidden: boolean
  index: number
  setHidden: (bool: boolean) => void
  col: string
  value: { [key: string]: string | number | null }
  inputState?: InputState
}

const DisabledCell: FC<{
  col: string
  value: { [key: string]: string | number | null }
  inputState: InputState
}> = ({ col, value, inputState }) => {
  return (
    <div
      className={`w-full p-2 text-center ${INPUT_STATE_CLASSES[inputState]}`}
      title="This field is disabled"
    >
      {value[col] !== null
        ? col.includes('Date')
          ? (() => {
            try {
              const parsedDate = parse(String(value[col]), 'yyyyMMdd', new Date())
              return isNaN(parsedDate.getTime()) ? '—' : format(parsedDate, 'yyyy-MM-dd')
            } catch {
              return '—'
            }
          })()
          : ['no2', 'nh3'].includes(col)
            ? String(Number(value[col]).toLocaleString('en-US')) + ' mg/L'
            : col === 'sampleWeight'
              ? String(Number(value[col]).toLocaleString('en-US')) + ' g'
              : col === 'feedingAmount'
                ? String(Number(value[col]).toLocaleString('en-US')) + ' kg'
                : String(Number(value[col]).toLocaleString('en-US'))
        : '—'}
    </div>
  )
}

export const InputBase = forwardRef<HTMLDivElement, IputBaseProps>(
  (
    { id, inputRefs, index, insertData, col, value, placeholder, disabled = false },
    ref,
  ) => {
    const [hidden, setHidden] = useState<boolean>(value[col] !== null)

    const inputState = disabled ? 'disabled' : hidden ? 'completed' : 'editing'

    if (inputState === 'disabled') {
      return (
        <div ref={ref}>
          <DisabledCell col={col} value={value} inputState={inputState} />
        </div>
      )
    }

    if (inputState === 'completed') {
      return (
        <div ref={ref}>
          <BaseCell
            hidden={hidden}
            setHidden={setHidden}
            col={col}
            value={value}
            inputRefs={inputRefs}
            index={index}
            inputState={inputState}
          />
        </div>
      )
    }

    // editing state
    return col.includes('Date') ? (
      <div ref={ref}>
        <CustomDatePickerWrapper
          setHidden={setHidden}
          id={id}
          insertData={insertData}
          col={col}
          value={value}
          inputState={inputState}
        />
      </div>
    ) : (
      <div ref={ref}>
        <BaseInput
          placeholder={placeholder}
          hidden={hidden}
          setHidden={setHidden}
          id={id}
          inputRefs={inputRefs}
          index={index}
          insertData={insertData}
          col={col}
          value={value}
          inputState={inputState}
        />
      </div>
    )
  },
)

InputBase.displayName = 'InputBase'

type BaseInputProps = {
  hidden: boolean
  setHidden: (bool: boolean) => void
  inputState: InputState
} & IputBaseProps

const BaseInput: FC<BaseInputProps> = ({
  hidden,
  id,
  value,
  col,
  placeholder,
  setHidden,
  insertData,
  index,
  inputRefs,
  inputState,
}) => {
  const isIntegerField = ['stock', 'feedTimes', 'mortality', 'harvest', 'count'].includes(col)

  return (
    <Input
      hidden={hidden}
      id={String(id)}
      defaultValue={value[col] !== null ? String(value[col]) : ''}
      size="sm"
      name={col}
      type={'number'}
      placeholder={placeholder}
      radius="none"
      min={0}
      max={isIntegerField ? 10000000 : undefined}
      step={isIntegerField ? 1 : 'any'}
      autoComplete="off"
      ref={(el) => (inputRefs.current[index] = el)}
      onBlur={(event) => {
        const inputValue = (event.target as HTMLInputElement).value
        const numValue = Number(inputValue)
        if (
          !inputValue ||
          (isIntegerField && (!Number.isInteger(numValue) || numValue > 10000000))
        ) {
          setHidden(false)
        } else {
          setHidden(true)
        }
        insertData({
          id: (event.target as HTMLInputElement).id,
          name: (event.target as HTMLInputElement).name,
          value: (event.target as HTMLInputElement).value.replace(/-/g, ''),
        })
      }}
      isInvalid={
        !col.includes('Date') && isIntegerField && typeof value[col] === 'number'
          ? !Number.isInteger(value[col]) || (value[col] as number) > 10000000
          : false
      }
      validate={(value) => (Number.isInteger(Number(value)) ? 'none' : true)}
      onKeyDown={(event) => {
        keyDownChange(event, index, inputRefs)
      }}
      classNames={{
        inputWrapper: `p-0 shadow-none group-data-[focus-visible=true]:ring-opacity-0 ${INPUT_STATE_CLASSES[inputState]}`,
        base: 'm-0 p-0',
        input: 'm-0 p-0 outline-none',
      }}
      style={{ textAlign: 'center' }}
      onWheel={(event) => event.currentTarget.blur()}
    />
  )
}

type CustomDatePickerWrapperProps = {
  setHidden: (bool: boolean) => void
  inputState: InputState
} & Omit<IputBaseProps, 'inputRefs' | 'index'>

const CustomDatePickerWrapper: FC<CustomDatePickerWrapperProps> = ({
  id,
  value,
  col,
  setHidden,
  insertData,
  inputState,
}) => {
  const [year, setYear] = useState<string>('')
  const [month, setMonth] = useState<string>('')
  const [day, setDay] = useState<string>('')
  const [showCalendar, setShowCalendar] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Initialize values from prop
  useEffect(() => {
    if (value[col]) {
      try {
        const dateStr = String(value[col])
        if (dateStr.length === 8) {
          setYear(dateStr.substring(0, 4))
          setMonth(dateStr.substring(4, 6))
          setDay(dateStr.substring(6, 8))
        }
      } catch {
        // Invalid date
      }
    }
  }, [value, col])

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowCalendar(false)
      }
    }

    if (showCalendar) {
      // Use 'click' instead of 'mousedown' to avoid conflict with button onClick
      // mousedown fires before onClick, causing the calendar to close before it can open
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showCalendar])

  const handleDateChange = (dateValue: number | null) => {
    if (dateValue === null) {
      insertData({
        id: String(id),
        name: col,
        value: '',
      })
      setHidden(false)
    } else {
      insertData({
        id: String(id),
        name: col,
        value: String(dateValue),
      })
      setHidden(true)
    }
  }

  const updateDate = (y: string, m: string, d: string) => {
    if (y.length === 4 && m.length === 2 && d.length === 2) {
      const dateStr = y + m + d
      try {
        const parsedDate = parse(dateStr, 'yyyyMMdd', new Date())
        if (!isNaN(parsedDate.getTime())) {
          handleDateChange(Number(dateStr))
          return
        }
      } catch {
        // Invalid date
      }
    }
    handleDateChange(null)
  }

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').substring(0, 4)
    setYear(val)
    updateDate(val, month, day)
  }

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').substring(0, 2)
    setMonth(val)
    updateDate(year, val, day)
  }

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').substring(0, 2)
    setDay(val)
    updateDate(year, month, val)
  }

  const getDateValue = () => {
    if (!value[col]) return null
    try {
      const parsedDate = parse(String(value[col]), 'yyyyMMdd', new Date())
      if (isNaN(parsedDate.getTime())) return null
      return parseDate(format(parsedDate, 'yyyy-MM-dd'))
    } catch {
      return null
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={`flex items-center gap-1 justify-center h-8 ${INPUT_STATE_CLASSES[inputState]}`}
      >
        <input
          type="text"
          value={year}
          onChange={handleYearChange}
          placeholder="yyyy"
          className="w-[40px] h-full text-center bg-transparent outline-none border-none font-normal"
          style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
          maxLength={4}
        />
        <span style={{ fontSize: 'inherit', lineHeight: 'inherit' }}>-</span>
        <input
          type="text"
          value={month}
          onChange={handleMonthChange}
          placeholder="mm"
          className="w-[28px] h-full text-center bg-transparent outline-none border-none font-normal"
          style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
          maxLength={2}
        />
        <span style={{ fontSize: 'inherit', lineHeight: 'inherit' }}>-</span>
        <input
          type="text"
          value={day}
          onChange={handleDayChange}
          placeholder="dd"
          className="w-[24px] h-full text-center bg-transparent outline-none border-none font-normal"
          style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
          maxLength={2}
        />
        <button
          type="button"
          onMouseDown={(e) => {
            // Prevent blur event on other inputs when clicking this button
            // This ensures the calendar opens on first click even when another input loses focus
            e.preventDefault()
          }}
          onClick={() => setShowCalendar(!showCalendar)}
          className="ml-1 h-full flex items-center p-0.5 hover:bg-gray-100 rounded"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </button>
      </div>

      <div
        className="absolute top-full left-0 z-50 mt-1 bg-white"
        style={{ display: showCalendar ? 'block' : 'none' }}
      >
        <I18nProvider locale="en-CA">
          <Calendar
            aria-label="Date selection"
            maxValue={today(getLocalTimeZone())}
            value={getDateValue() as any}
            onChange={(dateValue: any) => {
              if (!dateValue) {
                setYear('')
                setMonth('')
                setDay('')
                handleDateChange(null)
                setShowCalendar(false)
                return
              }
              const y = String(dateValue.year)
              const m = String(dateValue.month).padStart(2, '0')
              const d = String(dateValue.day).padStart(2, '0')
              setYear(y)
              setMonth(m)
              setDay(d)
              handleDateChange(Number(y + m + d))
              setShowCalendar(false)
            }}
          />
        </I18nProvider>
      </div>
    </div>
  )
}

const BaseCell: FC<BaseCellProps> = ({
  hidden,
  inputRefs,
  index,
  setHidden,
  col,
  value,
  inputState = 'completed',
}) => {
  return (
    <div
      className={`w-full ${INPUT_STATE_CLASSES[inputState]}`}
      onClick={() => {
        setHidden(false)

        setTimeout(() => {
          inputRefs.current[index]?.focus()
        }, 0)
      }}
      key={col}
      style={{
        textAlign: 'center',
        minWidth: col.includes('Date') ? '128px' : 'auto',
      }}
      hidden={!hidden}
      title="Click to edit"
    >
      {/* Edit icon (hover) */}
      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <svg
          width="10"
          height="10"
          viewBox="0 0 16 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="text-tdplindigo"
        >
          <path
            d="M0.5 11.875V15H3.625L12.8417 5.78334L9.71667 2.65834L0.5 11.875ZM15.2583 3.36667C15.5833 3.04167 15.5833 2.51667 15.2583 2.19167L13.3083 0.241675C12.9833 -0.0833252 12.4583 -0.0833252 12.1333 0.241675L10.6083 1.76667L13.7333 4.89167L15.2583 3.36667Z"
            fill="currentColor"
          />
        </svg>
      </div>

      {/* Value display */}
      <div className="px-6 py-1.5">
        {value[col] !== null
          ? col.includes('Date')
            ? (() => {
              try {
                const parsedDate = parse(String(value[col]), 'yyyyMMdd', new Date())
                return isNaN(parsedDate.getTime()) ? '—' : format(parsedDate, 'yyyy-MM-dd')
              } catch {
                return '—'
              }
            })()
            : ['no2', 'nh3'].includes(col)
              ? String(Number(value[col]).toLocaleString('en-US')) + ' mg/L'
              : col === 'sampleWeight'
                ? String(Number(value[col]).toLocaleString('en-US')) + ' g'
                : col === 'feedingAmount'
                  ? String(Number(value[col]).toLocaleString('en-US')) + ' kg'
                  : String(Number(value[col]).toLocaleString('en-US'))
          : null}
      </div>
    </div>
  )
}

type CustomDatePickerProps = {
  label: string
  value: number | null
  dateBorderColor: string
  setValue: (startDate: number | null) => void
  warnText?: string
}

export const CustomDatePicker: FC<CustomDatePickerProps> = ({
  label,
  value,
  dateBorderColor,
  setValue,
  warnText,
}) => {
  // Use local state to track the date value for controlled component
  const [localDate, setLocalDate] = useState<any>(undefined)
  const isInitialized = useRef(false)

  // Sync local state with external value only on initial mount or when value changes externally
  useEffect(() => {
    if (!value) {
      setLocalDate(undefined)
      return
    }
    try {
      const parsedDate = parse(String(value), 'yyyyMMdd', new Date())
      if (!isNaN(parsedDate.getTime())) {
        setLocalDate(parseDate(format(parsedDate, 'yyyy-MM-dd')))
      } else {
        setLocalDate(undefined)
      }
    } catch {
      setLocalDate(undefined)
    }
    isInitialized.current = true
  }, [value])

  const handleChange = (newValue: any) => {
    setLocalDate(newValue)
    if (!newValue) {
      setValue(null)
      return
    }
    const dateStr =
      String(newValue.year) +
      String(newValue.month).padStart(2, '0') +
      String(newValue.day).padStart(2, '0')
    if (dateStr.length === 8) {
      setValue(Number(dateStr))
    } else {
      setValue(null)
    }
  }

  return (
    <>
      <I18nProvider locale="en-CA">
        <DatePicker
          label={label}
          name="StartDate"
          labelPlacement="outside"
          radius="sm"
          size="lg"
          maxValue={today(getLocalTimeZone())}
          isInvalid={false}
          validationBehavior="native"
          value={localDate}
          className={`gap-y-0 font-bold [&>div]:bg-white [&>div]:rounded-md
        ${dateBorderColor}
        [&>div]:hover:bg-white [&>div]:gap-y-0
          [&>span]:text-base [&>span]:mt-3`}
          classNames={{
            selectorButton: '!w-[20px] !min-w-[20px] !max-w-[20px] !h-[20px] !p-6',
          }}
          data-testid="start-date-picker"
          aria-label={`${label} date picker`}
          onChange={handleChange}
        />
      </I18nProvider>
      {dateBorderColor.includes('tdplmaroon') ? (
        <p className="m-0 text-sm font-semibold text-tdplmaroon">
          {warnText !== undefined ? warnText : 'Please enter a Start Date.'}
        </p>
      ) : (
        <p></p>
      )}
    </>
  )
}
